import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, QrCode, ArrowLeftRight, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { StatusBadge, STATUS_CONFIG } from './estoque.utils';
import { useObras, useEstoquePermissions } from './useEstoqueConfig';
import EstoqueMovimentarModal from './EstoqueMovimentarModal';

export default function EstoqueListagem() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [obraFiltro, setObraFiltro] = useState('');
  const [page, setPage] = useState(1);
  const [movItem, setMovItem] = useState(null);

  const params = new URLSearchParams({ page, limit: 50, busca, status: statusFiltro, obra: obraFiltro }).toString();
  const { data, isLoading } = useQuery({
    queryKey: ['estoque-itens', page, busca, statusFiltro, obraFiltro],
    queryFn: () => api.get(`/estoque/itens?${params}`).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: cats } = useQuery({ queryKey: ['estoque-cats'], queryFn: () => api.get('/estoque/categorias').then(r => r.data.data) });
  const obras = useObras();
  const perm = useEstoquePermissions();

  const itens = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  function imprimirQR(item) {
    const url = `${window.location.origin}/estoque/scanner?token=${item.qrcode_token}`;
    const w = window.open('', '_blank', 'width=400,height=500');
    w.document.write(`<!DOCTYPE html><html><head><title>${item.codigo}</title>
    <style>
      body{font-family:Arial,sans-serif;margin:0;padding:10px;display:flex;flex-direction:column;align-items:center;}
      .logo{font-size:10px;color:#666;margin-bottom:4px;}
      .codigo{font-size:12px;font-weight:bold;margin-top:4px;}
      .nome{font-size:10px;color:#333;text-align:center;max-width:180px;margin-top:2px;}
      @media print{@page{size:60mm 40mm;margin:0;}body{padding:4mm;}}
    </style></head><body>
    <div class="logo">Inbase Construtora</div>
    <img src="${window.location.origin}/api/estoque/qrcode/${item.qrcode_token}/imagem" style="width:120px;height:120px;" />
    <div class="codigo">${item.codigo}</div>
    <div class="nome">${item.nome}</div>
    <script>window.onload=()=>{setTimeout(()=>window.print(),500);}</script>
    </body></html>`);
    w.document.close();
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Wrench size={24} className="text-blue-600" /> Ferramentas
          <span className="text-sm font-normal text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{total}</span>
        </h1>
        {perm.novo && (
          <button onClick={() => navigate('/estoque/novo')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={16} /> Novo Item
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => { setBusca(e.target.value); setPage(1); }} placeholder="Buscar por nome, código ou responsável..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
        </div>
        <select value={obraFiltro} onChange={e => { setObraFiltro(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">Todas as obras</option>
          {obras.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>

      {/* Status chips */}
      <div className="flex gap-2 flex-wrap">
        {[{ value: '', label: 'Todos' }, ...Object.entries(STATUS_CONFIG).map(([v, c]) => ({ value: v, label: c.label }))].map(s => (
          <button key={s.value} onClick={() => { setStatusFiltro(s.value); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              statusFiltro === s.value ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:border-gray-400'
            }`}>{s.label}</button>
        ))}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs">Código</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs">Nome</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs hidden md:table-cell">Categoria</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs hidden lg:table-cell">Obra / Local</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 text-xs hidden lg:table-cell">Responsável</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">Carregando...</td></tr>
              ) : itens.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">Nenhum item encontrado</td></tr>
              ) : itens.map(item => (
                <tr key={item.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-gray-600">{item.codigo}</span>
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/estoque/itens/${item.id}`)}>
                    <p className="font-medium text-gray-800 hover:text-blue-600">{item.nome}</p>
                    {item.marca && <p className="text-xs text-gray-400">{item.marca}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell text-xs">{item.categoria_nome || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                  <td className="px-4 py-3 text-gray-600 hidden lg:table-cell text-xs">{item.obra_atual || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell text-xs">{item.responsavel_atual || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => setMovItem(item)} title="Movimentar"
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                        <ArrowLeftRight size={15} />
                      </button>
                      <button onClick={() => imprimirQR(item)} title="Imprimir QR Code"
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                        <QrCode size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginação */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1.5 text-sm border rounded-xl disabled:opacity-40 hover:bg-gray-50">Anterior</button>
          <span className="text-sm text-gray-500">Página {page} de {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
            className="px-3 py-1.5 text-sm border rounded-xl disabled:opacity-40 hover:bg-gray-50">Próxima</button>
        </div>
      )}

      {movItem && (
        <EstoqueMovimentarModal
          item={movItem}
          onClose={() => setMovItem(null)}
          onSuccess={() => qc.invalidateQueries(['estoque-itens'])}
        />
      )}
    </div>
  );
}
